import React, {FC} from 'react';
import cn from 'classnames';
import Link, {LinkProps} from 'next/link';

import {AnchorButtonProps, ButtonProps, CustomButtonProps} from './types';
import {
  generateIconComponent,
  isAnchorButtonProps,
  isButtonProps,
  isLinkProps,
} from './utils';

import styles from './styles.module.scss';

const Button: FC<CustomButtonProps> = (props) => {
  const {
    size = 'medium',
    disabled = false,
    ariaDisabled = false,
    loading = false,
    withIcon = undefined,
    fitParent = false,
    config = {
      color: 'blue',
      type: 'primary',
    },
    as = 'button',
    withAnimation = false,
    children = undefined,
    className = undefined,
    onClick = undefined,
    ...rest
  } = props;

  const buttonClasses = cn(
    className,
    styles.button,
    styles[`button__${config?.color}`],
    styles[`button__mod${size.charAt(0).toUpperCase() + size.slice(1)}`],
    styles[`button__${config?.color}_${config?.type}`],
    {
      [styles[`button__${config?.color}_${config?.type}__modDisabled`]]:
        disabled || ariaDisabled,
      [styles.button__modLoading]: loading,
      [styles.button__modReverse]: withIcon?.iconPosition === 'left',
      [styles.button__modAnimation]: withAnimation,
    },
    {
      [styles.button__modFitParent]: fitParent,
    },
  );

  const iconComponent: React.ReactElement = generateIconComponent(
    withIcon,
    disabled,
  );

  if (isAnchorButtonProps(as)) {
    return (
      <a {...(rest as AnchorButtonProps)} className={buttonClasses}>
        {children}
        {iconComponent}
        {loading && <div className={styles.loader} />}
      </a>
    );
  }

  if (isButtonProps(as)) {
    // ariaDisabled deliberately still fires: the handler is what tells the
    // user why the action is unavailable.
    const onButtonClick = (e?: React.MouseEvent<HTMLButtonElement>) =>
      !disabled && !loading && onClick && onClick(e);

    return (
      <button
        {...(rest as ButtonProps)}
        type={(rest.type || 'button') as ButtonProps['type']}
        onClick={onButtonClick}
        className={buttonClasses}
        aria-disabled={ariaDisabled || undefined}
        disabled={disabled}>
        {children}
        {iconComponent}
        {loading && <div className={styles.loader} />}
      </button>
    );
  }

  if (isLinkProps(as)) {
    return (
      <Link className={buttonClasses} {...(rest as LinkProps)}>
        {children}
        {iconComponent}
        {loading && <div className={styles.loader} />}
      </Link>
    );
  }
};

export default Button;
